import moment from "moment"

export const dateTimeFormat = {
   casitaCustomDateFormat: "MMMM DD, YYYY - HH:mm",
   casitaCustomDateFormatOnly: "MMMM DD, YYYY",
   appDateTimeFormat: "DD/MM/YYYY, HH:mm A",
   appDateFormat: "DD-MM-YYYY",
   apiDateTimeFormat: "YYYY-MM-DD HH:mm:ss",
   apiDateFormat: "YYYY-MM-DD",
   gmtDateTimeFormat: "YYYY-MM-DD HH:mm",
   creationDateFormat: "DD MMMM, YYYY",
   onlyTimeFormat: "HH:mm A",
   apiTimeFormat: "HH:mm:ss",
   appTimeFormat: "HH:mm",
   payoutDateQuery: "YYYY-MM",
   timeFormat: "LT",
   dateWithTimeFormat: "MMM  Do, h:mm A",
   dateWithYearTimeFormat: "MMM  Do YYYY, h:mm A",
   dateRangeFormat: "MM/dd/yyyy",
   trailerDateFormat: "DD MMM, YYYY",
   rangeFormat: "MMM/DD/yyyy",
   americanFormat: "MM/DD/yyyy",
}

/**
 * Formats a timestamp in the viewer's own timezone.
 *
 * This used to force .utc() on both branches, so every date in the admin rendered in UTC
 * regardless of who was looking — which is what made submitted-build timestamps read 5-6 hours
 * ahead for the Central-time staff who use them.
 *
 * IMPORTANT: pass `utc: true` for date-only values. A Prisma `@db.Date` column arrives as
 * `2026-03-08T00:00:00.000Z`, and rendering that in a negative-offset zone would show the
 * previous day. Only genuine timestamps (`@db.Timestamptz`, i.e. createdAt/updatedAt and
 * friends) should use the local default.
 */
export const convertDateTime = ({
   to,
   date,
   dateOnly = false,
   customFormat = null,
   utc = false,
}) => {
   const format = customFormat
      ? customFormat
      : dateOnly
      ? dateTimeFormat.apiDateFormat
      : dateTimeFormat.apiDateTimeFormat

   return utc ? moment.utc(date).format(format) : moment(date).format(format)
}

/**
 * Viewer's timezone abbreviation, but only when a real one exists — "CST", "CDT", "EST".
 * Returns "" otherwise.
 *
 * Not every zone has an abbreviation in CLDR. Asia/Karachi resolves to "GMT+5" and
 * Asia/Kolkata to "GMT+5:30", which is just the offset restated and adds nothing next to a
 * time that is already shown in the viewer's own zone. Those cases return "" so the caller
 * renders the timestamp bare rather than labelling it with noise.
 */
export const getLocalTimeZoneAbbr = (date = new Date()) => {
   try {
      const part = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
         .formatToParts(date)
         .find(p => p.type === "timeZoneName")
      const value = part ? part.value : ""
      // "GMT+5", "GMT-04:00", bare "GMT", or "UTC" — an offset, not an abbreviation.
      return /^(GMT|UTC)([+-]|$)/.test(value) ? "" : value
   } catch (err) {
      return ""
   }
}

/**
 * Converts the days picked in a date-range control into the UTC bounds the API expects
 * (fromDate/toDate are parsed with moment.utc server-side).
 *
 * The picked days are the viewer's local days, matching the now-local timestamps in the table,
 * so start/end of day are taken in local time *first* and only then converted to UTC. The
 * previous code did .utc().startOf("day"), which snapped to UTC midnight after shifting and so
 * never lined up with the day the user actually clicked.
 */
/**
 * Day bounds for filtering a **date-only** column (Prisma `@db.Date`, e.g. TimelineCell.date).
 *
 * Deliberately does NOT shift by the viewer's offset. A date-only value is stored at
 * midnight UTC, so the picked calendar day must be sent as plain `YYYY-MM-DD 00:00:00` /
 * `23:59:59` — the API parses those as UTC and they then line up exactly with the stored value.
 * Applying the local-to-UTC conversion used for timestamps would move both bounds by the
 * offset and pull in or drop the adjacent day.
 *
 * Use getLocalDayRangeUtc instead when filtering a real timestamp such as createdAt.
 */
export const getCalendarDayRangeUtc = (startDate, endDate) => {
   const range = {}

   if (startDate) {
      const from = moment(startDate)
      if (from.isValid()) {
         range.from = `${from.format(dateTimeFormat.apiDateFormat)} 00:00:00`
      }
   }

   if (endDate) {
      const to = moment(endDate)
      if (to.isValid()) {
         range.to = `${to.format(dateTimeFormat.apiDateFormat)} 23:59:59`
      }
   }

   return range
}

export const getLocalDayRangeUtc = (startDate, endDate) => {
   const range = {}

   if (startDate) {
      const from = moment(startDate)
      if (from.isValid()) {
         range.from = from
            .startOf("day")
            .utc()
            .format(dateTimeFormat.apiDateTimeFormat)
      }
   }

   if (endDate) {
      const to = moment(endDate)
      if (to.isValid()) {
         range.to = to.endOf("day").utc().format(dateTimeFormat.apiDateTimeFormat)
      }
   }

   return range
}

export const convertTime = ({ tz, time, format }) => {
   if (tz === "GMT_TIME") {
      return moment(time, format).utc().format(format)
   } else if (tz === "LOCAL_TIME") {
      return moment.utc(time, format).local().format(format)
   }
}

export const convertDateTimeGMTFormat = date => {
   const startOf = () => {
      return moment(date).utc()
         .startOf("day")
         .format(dateTimeFormat.gmtDateTimeFormat)
   }
   const endOf = () => {
      return moment(date).utc().endOf("day").format(dateTimeFormat.gmtDateTimeFormat)
   }
   return {
      startOf,
      endOf,
   }
}

export const convertDateToInvoicePattern = date => {
   const getPreviousMonthDate = () => {
      return moment(date).subtract(1, "month").format("LL")
   }
   const getPreviousDayDate = () => {
      return moment(date).subtract(1, "day").format("LL")
   }
   return {
      getPreviousMonthDate,
      getPreviousDayDate,
   }
}

// Parses a time-of-day string and reformats it. The old .utc() here shifted the clock by the
// viewer's offset, so "14:30" could come back as "09:30".
export const getTimeWithSeconds = (time, format, requiredFormat) => {
   return moment(time, format).format(requiredFormat)
}

// "Today at 3:14 PM" style relative label — has to be evaluated in the viewer's own day.
export const getNearestTimeStampStr = date => {
   const isDaySame = moment().isSame(date, "day")
   const isYearSame = moment().isSame(date, "year")
   if (isDaySame) {
      return moment(date).format(dateTimeFormat.timeFormat)
   } else if (!isDaySame && isYearSame) {
      return moment(date).format(dateTimeFormat.dateWithTimeFormat)
   } else return moment(date).format(dateTimeFormat.dateWithYearTimeFormat)
}

export function parseDate(date) {
   if (!date) return null
   const newDate = date.replace(/Z$/, "")
   return new Date(newDate)
}
